<template>
  <div class="p2p-stack" style="gap:24px">
    <div v-if="!member" class="p2p-card is-accent">
      <h2 class="p2p-h2">Create an account to publish</h2>
      <router-link to="/register" class="p2p-btn" style="align-self:flex-start">Create my account</router-link>
    </div>
    <template v-else>
      <div class="p2p-seg" role="tablist" aria-label="How to publish">
        <button v-for="item in tabs" :key="item.key" type="button" role="tab" :aria-selected="tab === item.key ? 'true' : 'false'" :class="{ on: tab === item.key }" @click="setTab(item.key)">
          <v-icon small>{{ item.icon }}</v-icon> {{ item.label }}
        </button>
      </div>

      <div v-if="!accounts.length" class="p2p-alert">Choose at least one connected account first. <a href="#" @click.prevent="$emit('go', 'accounts', { batch: batchId })">Choose accounts</a></div>

      <!-- Publish now / schedule -->
      <section v-if="tab !== 'automate'" class="p2p-card">
        <div class="p2p-between">
          <h2 class="p2p-h2">{{ tab === 'now' ? 'Publish now' : 'Schedule these posts' }}</h2>
          <div class="p2p-row" style="gap:6px"><NetBadge v-for="account in accounts" :key="account.id" :platform="account.platform" /></div>
        </div>
        <div v-if="tab === 'schedule'" class="p2p-row" style="align-items:flex-end">
          <label class="p2p-label">Day<input v-model="day" type="date" class="p2p-input" :min="today"></label>
          <label class="p2p-label">Time<input v-model="time" type="time" class="p2p-input"></label>
          <label class="p2p-label">Spacing
            <select v-model.number="spacing" class="p2p-select">
              <option :value="0">All at once</option><option :value="24">One post per day</option>
              <option :value="48">Every two days</option><option :value="168">One post per week</option>
            </select>
          </label>
          <span class="p2p-faint" style="padding-bottom:12px">Times are in {{ timezone }}</span>
        </div>
        <ul class="p2p-plan">
          <li v-for="(item, index) in plan" :key="item.post.id" class="p2p-plan-row">
            <MediaImg v-if="item.post.media.length" :src="item.post.media[0].poster_url || item.post.media[0].url" ratio="4 / 5" class="p2p-plan-thumb" />
            <span v-else class="p2p-plan-thumb text"><v-icon small>mdi-text</v-icon></span>
            <div class="p2p-stack" style="gap:4px;min-width:0;flex:1 1 auto">
              <div class="p2p-h3" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ item.post.content.hook }}</div>
              <div class="p2p-row" style="gap:6px">
                <span v-for="account in item.targets" :key="account.id" class="p2p-row" style="gap:4px"><NetBadge :platform="account.platform" /></span>
                <span v-if="item.skipped.length" class="p2p-faint">· skipped on {{ item.skipped.join(', ') }}</span>
              </div>
            </div>
            <span class="p2p-faint p2p-mono">{{ tab === 'now' ? 'now' : slotLabel(index) }}</span>
          </li>
        </ul>
        <div v-if="claimsPending" class="p2p-alert">
          <label class="p2p-check"><input v-model="confirmClaims" type="checkbox">
            {{ claimsPending }} post{{ claimsPending === 1 ? '' : 's' }} mention{{ claimsPending === 1 ? 's' : '' }} something your site does not confirm. I have checked them.</label>
        </div>
        <div class="p2p-between">
          <span class="p2p-muted">{{ pairs }} publish{{ pairs === 1 ? '' : 'es' }} · {{ pairs }} credit{{ pairs === 1 ? '' : 's' }}, charged only when a network confirms the post.</span>
          <button type="button" class="p2p-btn lg" :disabled="!canSubmit" @click="submit">
            {{ busy ? 'Working…' : tab === 'now' ? 'Publish now' : 'Schedule ' + plan.length + ' post' + (plan.length === 1 ? '' : 's') }}
          </button>
        </div>
      </section>

      <!-- Automate -->
      <section v-else class="p2p-card">
        <div class="p2p-stack" style="gap:6px">
          <h2 class="p2p-h2">Autopilot for {{ project.name }}</h2>
          <p class="p2p-text">We write a fresh post from your site before each slot, so your feed keeps moving without you. You approve each one first, unless you turn that off.</p>
        </div>
        <div class="p2p-label">Days
          <div class="p2p-row" style="gap:6px">
            <button v-for="day in days" :key="day.key" type="button" :class="['p2p-chip', { 'is-accent': auto.days.indexOf(day.key) >= 0 }]" :aria-pressed="auto.days.indexOf(day.key) >= 0 ? 'true' : 'false'" style="cursor:pointer;min-width:52px;justify-content:center" @click="toggleDay(day.key)"><v-icon v-if="auto.days.indexOf(day.key) >= 0" x-small>mdi-check</v-icon>{{ day.label }}</button>
          </div>
        </div>
        <div class="p2p-row" style="align-items:flex-end">
          <label class="p2p-label">Time<input v-model="auto.time_local" type="time" class="p2p-input"></label>
          <label class="p2p-label" style="flex:1 1 260px">Time zone
            <select v-model="auto.timezone" class="p2p-select"><option v-for="zone in zones" :key="zone" :value="zone">{{ zone }}</option></select>
          </label>
        </div>
        <div class="p2p-label">Formats, taken in turn
          <div class="p2p-row" style="gap:6px">
            <button v-for="item in formatOptions" :key="item.key" type="button" :class="['p2p-chip', { 'is-accent': auto.formats.indexOf(item.key) >= 0 }]" :aria-pressed="auto.formats.indexOf(item.key) >= 0 ? 'true' : 'false'" style="cursor:pointer" @click="toggleFormat(item.key)"><v-icon v-if="auto.formats.indexOf(item.key) >= 0" x-small>mdi-check</v-icon>{{ item.label }}</button>
          </div>
        </div>
        <div class="p2p-label">Before each post
          <div class="p2p-seg" role="group" aria-label="Approval">
            <button type="button" :class="{ on: auto.approval_mode === 'review' }" :aria-pressed="auto.approval_mode === 'review' ? 'true' : 'false'" @click="auto.approval_mode = 'review'">I approve it first</button>
            <button type="button" :class="{ on: auto.approval_mode === 'auto' }" :aria-pressed="auto.approval_mode === 'auto' ? 'true' : 'false'" @click="auto.approval_mode = 'auto'">Publish automatically</button>
          </div>
          <span class="p2p-faint">{{ auto.approval_mode === 'review' ? 'Each post waits in Approvals until ' + approvalWindow + ' before its slot. Unapproved posts are skipped, never published.' : 'Posts publish on their own when they pass the checks: only facts from your site, and a fit for every network. Anything else waits for you.' }}</span>
        </div>
        <div class="p2p-row" style="gap:6px"><span class="p2p-faint">Publishing to</span><NetBadge v-for="account in accounts" :key="account.id" :platform="account.platform" /></div>
        <div class="p2p-faint">Posts use this project's picture style: {{ styleSummary }}. Change it in Create posts.</div>
        <details class="p2p-customize" :open="customized">
          <summary class="p2p-h3">Customize</summary>
          <div class="p2p-stack" style="gap:14px;padding-top:10px">
            <div class="p2p-row" style="align-items:flex-end">
              <label class="p2p-label">Voice
                <select v-model="auto.tone" class="p2p-select"><option :value="null">This project's voice</option>
                  <option value="founder">Founder</option><option value="professional">Professional</option><option value="playful">Playful</option><option value="bold">Bold</option></select>
              </label>
              <label class="p2p-label">Posts each time
                <select v-model.number="auto.posts_per_run" class="p2p-select"><option :value="1">1</option><option :value="2">2, two hours apart</option><option :value="3">3, two hours apart</option></select>
              </label>
              <label class="p2p-label">Re-read your site
                <select v-model="auto.refresh" class="p2p-select"><option value="weekly">Once a week</option><option value="each_run">Before every post</option><option value="never">Never</option></select>
              </label>
            </div>
            <div class="p2p-row" style="align-items:flex-end">
              <label class="p2p-label">Approvals close
                <select v-model.number="auto.approval_hours" class="p2p-select"><option :value="1">1 hour before</option><option :value="4">4 hours before</option><option :value="24">A day before</option></select>
              </label>
              <label class="p2p-label">Monthly credit limit
                <input v-model.number="auto.monthly_cap" type="number" min="0" step="1" class="p2p-input" style="max-width:160px" placeholder="No limit">
              </label>
            </div>
            <div class="p2p-label">Quiet periods <span class="p2p-faint">no posts on these days</span>
              <div v-for="(period, i) in auto.quiet_periods" :key="i" class="p2p-row" style="flex-wrap:nowrap">
                <input v-model="period.start" type="date" class="p2p-input" :aria-label="'Quiet period ' + (i + 1) + ' start'">
                <span class="p2p-faint">to</span>
                <input v-model="period.end" type="date" class="p2p-input" :aria-label="'Quiet period ' + (i + 1) + ' end'">
                <button type="button" class="p2p-icon-btn" :aria-label="'Remove quiet period ' + (i + 1)" @click="auto.quiet_periods.splice(i, 1)"><v-icon small>mdi-close</v-icon></button>
              </div>
              <button v-if="auto.quiet_periods.length < 10" type="button" class="p2p-btn ghost sm" style="align-self:flex-start" @click="auto.quiet_periods.push({ start: today, end: today })">+ Add a quiet period</button>
            </div>
          </div>
        </details>
        <div class="p2p-alert is-info">{{ bestImages ? 'Up to' : 'About' }} {{ weeklyCredits }} credits a week ({{ weeklyPosts }} post{{ weeklyPosts === 1 ? '' : 's' }} to {{ accounts.length }} account{{ accounts.length === 1 ? '' : 's' }}). If credits run out or your plan ends, autopilot pauses and tells you.</div>
        <div class="p2p-row">
          <button type="button" class="p2p-btn lg" :disabled="!canAutomate" @click="saveAutomation">{{ busy ? 'Saving…' : existing ? 'Update autopilot' : 'Turn on autopilot' }}</button>
        </div>
      </section>
      <div v-if="error" class="p2p-alert bad">{{ error }}</div>
    </template>
  </div>
</template>

<script>
import MediaImg from '../MediaImg.vue';
import NetBadge, { LABELS } from '../NetBadge.vue';
import { get, post, put, message, code } from '../../api';
import { localDate } from '../../poll';

const DAYS = [{ key: 'mon', label: 'Mon' }, { key: 'tue', label: 'Tue' }, { key: 'wed', label: 'Wed' }, { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' }, { key: 'sat', label: 'Sat' }, { key: 'sun', label: 'Sun' }];

function pad(value) { return String(value).padStart(2, '0'); }

export default {
  name: 'StepPublish',
  components: { MediaImg, NetBadge },
  props: { project: { type: Object, required: true }, config: { type: Object, default: null }, member: { type: Boolean, default: false } },
  data() {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const tomorrow = new Date(Date.now() + 24 * 3600 * 1000);
    return { posts: [], busy: false, error: '', confirmClaims: false, spacing: 24,
      day: tomorrow.getFullYear() + '-' + pad(tomorrow.getMonth() + 1) + '-' + pad(tomorrow.getDate()), time: '09:30', timezone: zone,
      auto: { days: ['mon', 'wed', 'fri'], time_local: '09:30', timezone: zone, formats: ['text', 'image', 'carousel'], approval_mode: 'review',
        tone: null, posts_per_run: 1, refresh: 'weekly', approval_hours: 1, monthly_cap: 0, quiet_periods: [] },
      days: DAYS, existing: null,
      tabs: [{ key: 'now', label: 'Publish now', icon: 'mdi-send-outline' }, { key: 'schedule', label: 'Schedule', icon: 'mdi-calendar-clock-outline' },
        { key: 'automate', label: 'Automate', icon: 'mdi-autorenew' }],
      formatOptions: [{ key: 'text', label: 'Text' }, { key: 'image', label: 'Image' }, { key: 'carousel', label: 'Carousel' }, { key: 'video', label: 'Motion video' }] };
  },
  computed: {
    tab() { return ['now', 'schedule', 'automate'].indexOf(this.$route.query.tab) >= 0 ? this.$route.query.tab : 'now'; },
    approvalWindow() { return { 1: 'an hour', 4: 'four hours', 24: 'a day' }[this.auto.approval_hours] || 'an hour'; },
    batchId() { return this.$route.query.batch || (this.project.batches && this.project.batches.length ? String(this.project.batches[0].id) : ''); },
    today() { const d = new Date(); return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); },
    accounts() {
      const ids = (this.project.settings && this.project.settings.account_ids) || [];
      return this.$store.getters['publisher/connected'].filter(account => ids.indexOf(account.id) >= 0);
    },
    plan() {
      return this.posts.map(item => {
        const targets = this.accounts.filter(account => (item.networks[account.platform] || {}).ok);
        const skipped = this.accounts.filter(account => !(item.networks[account.platform] || {}).ok).map(account => LABELS[account.platform]);
        return { post: item, targets, skipped };
      }).filter(item => item.targets.length);
    },
    pairs() { return this.plan.reduce((sum, item) => sum + item.targets.length, 0); },
    claimsPending() { return this.plan.filter(item => item.post.claims_pending > 0).length; },
    startAt() { return this.day && this.time ? new Date(this.day + 'T' + this.time) : null; },
    canSubmit() {
      if (this.busy || !this.pairs) return false;
      if (this.claimsPending && !this.confirmClaims) return false;
      if (this.tab === 'schedule' && (!this.startAt || this.startAt.getTime() < Date.now() + 3 * 60 * 1000)) return false;
      return this.plan.every(item => item.post.format === 'text' || item.post.visual_status === 'ready');
    },
    zones() {
      const list = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
      return list.length ? list : [this.auto.timezone, 'UTC'];
    },
    imageMode() {
      const formats = (this.project.settings && this.project.settings.formats) || {};
      return ['best', 'mine', 'ai'].indexOf(formats.image_mode) >= 0 ? formats.image_mode : 'best';
    },
    // 'Best for each post' charges each picture as what it turns out to be, so the weekly figure is an upper bound.
    bestImages() { return this.imageMode === 'best' && this.auto.formats.indexOf('image') >= 0; },
    weeklyCredits() {
      const average = { text: 1, image: this.imageMode === 'mine' ? 2 : 6, carousel: 12, video: 8 };
      const formats = this.auto.formats.length ? this.auto.formats : ['text'];
      const each = formats.reduce((sum, key) => sum + average[key], 0) / formats.length;
      return Math.round(this.weeklyPosts * (each + this.accounts.length));
    },
    weeklyPosts() { return this.auto.days.length * (Number(this.auto.posts_per_run) || 1); },
    styleSummary() {
      const formats = (this.project.settings && this.project.settings.formats) || {};
      const shape = { portrait: 'portrait 4:5', square: 'square 1:1', landscape: 'landscape', story: 'story 9:16' }[formats.shape || 'portrait'];
      const pictures = { best: 'your real pictures where they fit, AI pictures otherwise', mine: 'your own images', ai: 'AI pictures' }[this.imageMode];
      return pictures + ', ' + shape + ', ' + (formats.slides || 5) + '-slide carousels';
    },
    customized() {
      const a = this.auto;
      return !!(a.tone || a.posts_per_run > 1 || a.refresh !== 'weekly' || a.approval_hours > 1 || a.monthly_cap || a.quiet_periods.length);
    },
    canAutomate() { return !this.busy && this.auto.days.length && this.auto.formats.length && this.accounts.length && /^\d\d:\d\d$/.test(this.auto.time_local); }
  },
  async mounted() {
    if (!this.member) return;
    try {
      await this.$store.dispatch('publisher/loadAccounts');
      if (this.batchId) {
        const data = await get('/projects/' + this.project.id + '/posts', { batch_id: this.batchId });
        this.posts = data.posts.filter(item => item.selected);
      }
      if (this.project.automation) {
        const a = this.project.automation;
        this.existing = a;
        const s = a.settings || {};
        this.auto = { days: a.days, time_local: a.time_local, timezone: a.timezone, formats: a.formats, approval_mode: a.approval_mode,
          tone: s.tone || null, posts_per_run: s.posts_per_run || 1, refresh: s.refresh || 'weekly', approval_hours: s.approval_hours || 1,
          monthly_cap: s.monthly_cap || 0, quiet_periods: (s.quiet_periods || []).map(p => ({ start: p.start, end: p.end })) };
      }
    } catch (error) {
      this.error = message(error);
    }
  },
  methods: {
    setTab(key) { this.$router.replace({ query: { ...this.$route.query, tab: key } }).catch(() => {}); },
    slotLabel(index) {
      if (!this.startAt) return '';
      return localDate(new Date(this.startAt.getTime() + index * this.spacing * 3600 * 1000));
    },
    toggleDay(key) { const i = this.auto.days.indexOf(key); if (i >= 0) this.auto.days.splice(i, 1); else this.auto.days.push(key); },
    toggleFormat(key) { const i = this.auto.formats.indexOf(key); if (i >= 0) this.auto.formats.splice(i, 1); else this.auto.formats.push(key); },
    async submit() {
      this.busy = true;
      this.error = '';
      try {
        const payload = { post_ids: this.plan.map(item => item.post.id), account_ids: this.accounts.map(account => account.id),
          mode: this.tab === 'now' ? 'now' : 'schedule', confirm_claims: this.confirmClaims };
        if (this.tab === 'schedule') { payload.start_at = this.startAt.toISOString(); payload.spacing_hours = this.spacing; }
        const result = await post('/publish', payload);
        this.$store.dispatch('publisher/loadWorkspace');
        this.$router.push('/create/' + this.project.id + '/results/' + result.publish_group).catch(() => {});
      } catch (error) {
        this.error = message(error);
        if (code(error) === 'claims_unconfirmed') this.confirmClaims = false;
      } finally {
        this.busy = false;
      }
    },
    async saveAutomation() {
      this.busy = true;
      this.error = '';
      try {
        const formats = (this.project.settings && this.project.settings.formats) || {};
        const visual = { image_mode: this.imageMode, slides: formats.slides || 5, shape: formats.shape || 'portrait' };
        const options = { ...this.auto, monthly_cap: Math.max(0, Math.floor(Number(this.auto.monthly_cap) || 0)),
          quiet_periods: this.auto.quiet_periods.filter(p => p.start && p.end) };
        await put('/projects/' + this.project.id + '/automation', { ...options, ...visual, account_ids: this.accounts.map(account => account.id), status: 'active' });
        this.$router.push('/projects/' + this.project.id + '?tab=automation').catch(() => {});
      } catch (error) {
        this.error = message(error);
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>

<style>
.p2p-plan{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:8px}
.p2p-plan-row{display:flex;align-items:center;gap:12px;padding:10px;border:1px solid var(--p2p-line);border-radius:12px;background:var(--p2p-card)}
.p2p-plan-thumb{flex:0 0 auto;width:44px!important;border-radius:8px}
.p2p-plan-thumb.text{display:inline-grid;place-items:center;height:55px;background:var(--p2p-raised);color:var(--p2p-muted)}
</style>
